import { Link, useNavigate } from "react-router-dom";
import {
  RefreshCw,
  Home,
  ChevronLeft,
  FileQuestion,
  Video
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "../../components/ui/button";

export default function NotFound() {
  const navigate = useNavigate();

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center bg-slate-50 text-slate-900 px-6 relative overflow-hidden font-sans">
      {/* Soft Background Accents */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg aspect-square bg-indigo-50 blur-[150px] rounded-full pointer-events-none opacity-60"></div>

      <div className="relative z-10 text-center space-y-10 max-w-2xl">

        {/* Large 404 Visual */}
        <div className="relative inline-block">
          <motion.h1
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-[120px] md:text-[180px] font-black leading-none tracking-tighter text-slate-200"
          >
            404
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="absolute inset-0 flex items-center justify-center translate-y-4"
          >
            <div className="w-24 h-24 md:w-32 md:h-32 bg-white border border-slate-200 rounded-[32px] shadow-2xl flex items-center justify-center text-indigo-600 rotate-12">
              <FileQuestion size={48} strokeWidth={2.5} />
            </div>
          </motion.div>
        </div>

        {/* Friendly Message */}
        <div className="space-y-3">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-2xl md:text-3xl font-bold text-slate-900"
          >
            Page not found
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-slate-500 font-medium max-w-md mx-auto"
          >
            The link you followed might be broken, or the page may have moved. Let’s get you back to your studio.
          </motion.p>
        </div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4"
        >
          <Button
            asChild
            className="h-12 px-8 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold transition-all shadow-md shadow-indigo-100 flex items-center gap-2"
          >
            <Link to="/">
              <Home size={18} />
              Go back home
            </Link>
          </Button>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="h-12 px-6 rounded-xl border border-slate-200 text-slate-600 hover:bg-white hover:text-slate-900 font-bold transition-all"
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              Go back
            </Button>
            <Button
              variant="ghost"
              onClick={() => window.location.reload()}
              className="h-12 w-12 rounded-xl border border-slate-200 text-slate-500 hover:bg-white hover:text-slate-900 transition-all p-0"
            >
              <RefreshCw size={18} />
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Subtle Bottom Branding */}
      <div className="absolute bottom-10 flex items-center gap-2 opacity-50">
        <div className="w-6 h-6 bg-indigo-600 rounded-lg flex items-center justify-center">
          <Video size={12} className="text-white fill-white" />
        </div>
        <span className="text-sm font-bold tracking-tight text-slate-900 uppercase">VidLaya</span>
      </div>
    </div>
  );
}
